// InstanceStateApplicator
//
// Bridges ViewerStateStore (logical state by uniqueId) to actual
// InstancedMesh GPU buffers (instanceMatrix, instanceColor). Three
// entry points:
//
//   1. applyAll(instancedMesh)  — full sweep, called after materialize
//      or after a "full-sweep" store event (clear-isolate, isolate-on,
//      etc.). Walks every instance, recomputes effective state, writes
//      matrix + color.
//
//   2. applyForUids(adapter, uids) — targeted update, called for
//      hide(uid)/show(uid)/highlight(uid). Looks up where each uid is
//      currently rendering via the adapter, applies just that slot.
//      Cheap (one matrix + one color write per uid).
//
//   3. applyForUidInOneMesh(instancedMesh, uid) — single-mesh helper
//      used by MapMeshesAdapter when promoting a uid from a hidden
//      slot to an active slot — needs the new active slot to reflect
//      current store state, not blindly restored from snapshot.
//
// === MATRIX SOURCE: snapshotMatrices ===
//
// Each InstancedMesh carries a Float32Array snapshot of "what the
// matrix WOULD be if everything were visible" — the original
// per-instance world matrices, with C# disabled flag accounted for
// (disabled rows are stored as zero-scale, never recovered to
// non-zero).
//
// Apply writes either:
//   - snapshot[i] (visible)
//   - zero-scale  (hidden by store, isolated out, OR disabled by C#)
//
// Stored on `instancedMesh.userData.snapshotMatrices`. Materializer
// MUST set this; applicator reads it. Without snapshot, "show after
// hide" can't restore the original matrix and the instance stays
// invisible.
//
// (Why not reuse the existing instancedMesh.centeredMatrices? That
// field IS the snapshot — same data, same purpose. We unify under
// snapshotMatrices and keep centeredMatrices as a back-pointer for
// legacy code that reads centeredMatrices directly.)
//
// === COLOR SOURCE: baseColors + highlight ===
//
// Each InstancedMesh carries a Float32Array snapshot of "what the
// color WOULD be without highlights" — usually all white (1,1,1) but
// per-instance variation is possible if the materializer wants to
// encode subproject color tinting at the instance level.
//
// Apply writes either:
//   - baseColors[i]                 (no highlight)
//   - store.highlights.get(uid)     (highlighted)
//
// Stored on `instancedMesh.userData.baseColors`. Materializer sets it.

import { Matrix4, Color } from 'three';
import { writeGhostAt } from './ghostMaterial';
import { markSceneChanged } from './renderSignal';
import { markFinal } from './stateColor';
import { applyRanges } from './instanceUploadRanges';
import { partsOf } from './nestedParts';

const ZERO_MATRIX = new Matrix4().makeScale(0.0001, 0.0001, 0.0001);
const DEFAULT_COLOR = new Color(1, 1, 1);

// === GHOSTED VISUAL ===
//
// The "disabled" bucket renders as ghosted: gray tint via the
// per-instance color buffer. The pool's standard material samples
// instanceColor as a multiplicative tint, so writing a desaturated
// gray gives the geometry the obviously-inert look the precedence
// model calls for.
//
// True transparency (alpha < 1) would require a transparent material
// variant with depthWrite=false and proper sort order. That's out of
// scope here. The gray tint is sufficient to communicate "this is
// disabled" without changing the material pipeline.
const GHOSTED_COLOR = new Color(0.45, 0.45, 0.45);

// When a uid is both ghosted AND highlighted, the highlight should
// still read as highlighted (the user picked it for a reason) but
// muted so the disabled state is visible. Blend toward gray.
function _blendForGhostedHighlight(out, highlight) {
  // 50/50 mix of highlight and gray. Keeps the highlight hue
  // recognizable while obviously reducing intensity.
  out.r = highlight.r * 0.5 + GHOSTED_COLOR.r * 0.5;
  out.g = highlight.g * 0.5 + GHOSTED_COLOR.g * 0.5;
  out.b = highlight.b * 0.5 + GHOSTED_COLOR.b * 0.5;
  return out;
}

export default class InstanceStateApplicator {
  constructor({ store }) {
    if (!store) throw new Error('InstanceStateApplicator: store required');
    this.store = store;

    // Reusable temporaries for hot paths.
    this._tmpMatrix = new Matrix4();
    this._tmpColor = new Color();
  }

  // ============================================================
  // composeSlotState — single source of truth for slot rendering.
  //
  // Precedence (per product spec):
  //
  //   hidden       → visible:false, drop ghosted, drop highlight
  //   isolated-out → visible:false, drop ghosted, drop highlight
  //                  (uid not in current isolation set, with isolation
  //                   active)
  //   disabled-by-exporter (unless overridden by disabledShown):
  //                → visible:false (same as legacy hide-by-exporter)
  //   disabled     → visible:true, ghosted:true. Highlight still
  //                  composes ON TOP (muted toward gray) so the user
  //                  can see which ghosted item they picked.
  //   highlight    → visible:true, highlightColor:<color>
  //   else         → visible:true, normal.
  //
  // disabledByExporter is the per-slot sidecar flag from the C#
  // pipeline. We pass it in (the caller already has it from userData)
  // rather than re-reading from the store — the store doesn't carry
  // per-slot exporter metadata.
  //
  // Returns: { visible, ghosted, highlightColor }
  //   visible:        boolean
  //   ghosted:        boolean (true only when visible)
  //   highlightColor: Color | null (only set when visible AND a
  //                   highlight is registered; when ghosted, the
  //                   color is pre-blended toward gray)
  // ============================================================
  // `hash` is the fragment hash of the pool this slot lives in. An
  // isolation may be a RULE rather than a list (isolationRule.js), and a
  // rule answers for a pooled element from its fragment - a whole category
  // is a few thousand hashes where it was a million ids. A Set ignores the
  // second argument, so passing it costs nothing when it is not needed.
  // Merged meshes have no hash and are answered from the rule's uid list.
  composeSlotState(uid, disabledByExporter = false, hash = undefined) {
    const store = this.store;

    // 1. hidden — top precedence.
    // isHidden asks the list first and a rule second: hiding a whole
    // category no longer needs a million ids (third instance of
    // list-vs-rule tonight - see ViewerStateStore.setHiddenRule).
    if (store.isHidden ? store.isHidden(uid, hash) : store.hidden.has(uid)) {
      return { visible: false, ghosted: false, highlightColor: null };
    }

    // 2. isolated-out — when isolation is active and uid is NOT in it.
    if (store.isolated !== null && !store.isolated.has(uid, hash)) {
      return { visible: false, ghosted: false, highlightColor: null };
    }

    // 3. exporter-disabled (unless user overrode via disabledShown).
    //    Same semantic as before: invisible until user reveals.
    if (disabledByExporter && !store.disabledShown.has(uid)) {
      return { visible: false, ghosted: false, highlightColor: null };
    }

    // 4. ghosted (user-driven disabled bucket), or everything that
    //    carries no highlight while the store ghosts by default.
    // getHighlight consults the Map first and a rule second, so a whole
    // category can be selection-coloured without enumerating a million ids.
    // ASKED ONCE: it was asked twice per slot (once for the ghost decision,
    // once for the colour) with the same arguments, and this is the hottest
    // line of a full sweep - getHighlight under composeSlotState was 3-8 s
    // of main thread in a BA03 load on a real GPU (2026-10-13).
    const rawHighlight = store.getHighlight ? store.getHighlight(uid, hash) : (store.highlights.get(uid) || null);
    const ghosted = store.disabled.has(uid) || (store.ghostByDefault && !rawHighlight);

    // 5. highlight (composes on top of ghosted, muted).
    let highlightColor = null;
    if (rawHighlight) {
      if (ghosted) {
        const blended = new Color();
        _blendForGhostedHighlight(blended, rawHighlight);
        highlightColor = blended;
      } else {
        highlightColor = rawHighlight;
      }
    }

    return { visible: true, ghosted, highlightColor };
  }

  // ============================================================
  // FULL SWEEP — called after materialize, after isolate-on/off
  // ============================================================
  //
  // adapter (optional): when provided, enforces the single-active-
  // instance invariant — non-active duplicate slots are matrix-zeroed
  // regardless of store visibility. Without the adapter (legacy
  // call sites that don't have one threaded through), every slot
  // whose uid is visible-in-store gets the snapshot matrix, which
  // re-renders duplicates and Z-fights with the active slot.
  // _fullSweep always passes it; materializer's post-batch sweep also
  // passes it now.
  applyAll(instancedMesh, adapter = null) {
    if (!instancedMesh) return;
    // A tile batch (BatchedMesh, see tileBatch.js) is swept exactly like
    // a pool: it carries the same userData rows, the same snapshot
    // matrices, and setMatrixAt/setColorAt with the same signatures. It
    // counts its items separately because a BatchedMesh has no `count`.
    // A tile batch (BatchedMesh) or a tile's merged singleton mesh: both
    // carry the same userData rows and snapshot matrices and both accept
    // setMatrixAt/setColorAt, so both are swept exactly like a pool.
    const isTileBatch = !!(instancedMesh.isBatchedMesh || instancedMesh.isSlotMesh);
    if (!instancedMesh.isInstancedMesh && !isTileBatch) return;

    const userData = instancedMesh.userData;
    const snapshot = userData?.snapshotMatrices;
    if (!snapshot) {
      console.warn('[InstanceStateApplicator] no snapshotMatrices on mesh', instancedMesh.name);
      return;
    }

    // userData is an array of {uniqueId, disabled, ...} keyed by index
    // (set by InstanceMaterializer.materialize). We use the array,
    // not Object.entries (which would also enumerate the named
    // metadata fields like centeredMatrices/currentLod/hash/tileKey).
    const count = isTileBatch ? (instancedMesh.batchItemCount || 0) : instancedMesh.count;

    for (let i = 0; i < count; i++) {
      this._applyOne(instancedMesh, i, snapshot, userData, adapter);
    }

    // A whole-mesh sweep uploads the whole buffer — and must first drop any
    // ranges applyForUids left pending, because in three's updateBuffer a
    // non-empty updateRanges SUPPRESSES the full upload. A leftover range
    // here would strand stale instances on the GPU, which reads exactly
    // like "hide didn't hide".
    if (instancedMesh.instanceMatrix.clearUpdateRanges) instancedMesh.instanceMatrix.clearUpdateRanges();
    instancedMesh.instanceMatrix.needsUpdate = true;
    markSceneChanged('applyAll');
    if (instancedMesh.instanceColor) {
      if (instancedMesh.instanceColor.clearUpdateRanges) instancedMesh.instanceColor.clearUpdateRanges();
      instancedMesh.instanceColor.needsUpdate = true;
    }
  }

  // ============================================================
  // SLOT SWEEP — the same per-slot write as applyAll, for named slots
  // ============================================================
  //
  // For a caller that changed only SOME slots of a mesh and needs store
  // state laid back over exactly those: StreamingController._frustumShow
  // restoring one tile's slots in a pool that every tile of the model
  // shares. It used to applyAll the whole pool for every re-shown tile, so
  // a tick that re-showed N tiles swept the big pools N times over - one
  // tick after an orbit was 15-17 s of main thread on BA03 (real GPU,
  // 2026-10-13: tick -> _frustumShow -> applyAll -> _applyOne ->
  // composeSlotState). The other slots of the pool were not touched by the
  // restore, so re-deriving them was work with no effect.
  //
  // Uploads the whole buffer like applyAll does (and clears pending ranges
  // for the same reason): the CPU cost is the loop, not the upload flag.
  applySlots(instancedMesh, indices, adapter = null) {
    if (!instancedMesh || !indices || !indices.length) return;
    const isTileBatch = !!(instancedMesh.isBatchedMesh || instancedMesh.isSlotMesh);
    if (!instancedMesh.isInstancedMesh && !isTileBatch) return;
    const userData = instancedMesh.userData;
    const snapshot = userData?.snapshotMatrices;
    if (!snapshot) return;
    const count = isTileBatch ? (instancedMesh.batchItemCount || 0) : instancedMesh.count;
    for (const i of indices) {
      if (i >= 0 && i < count) this._applyOne(instancedMesh, i, snapshot, userData, adapter);
    }
    if (instancedMesh.instanceMatrix.clearUpdateRanges) instancedMesh.instanceMatrix.clearUpdateRanges();
    instancedMesh.instanceMatrix.needsUpdate = true;
    markSceneChanged('applySlots');
    if (instancedMesh.instanceColor) {
      if (instancedMesh.instanceColor.clearUpdateRanges) instancedMesh.instanceColor.clearUpdateRanges();
      instancedMesh.instanceColor.needsUpdate = true;
    }
  }

  // ============================================================
  // TARGETED UPDATE — for hide/show/highlight on specific uids
  // ============================================================

  // adapter: MapMeshesAdapter (provides slot lookup for a uid)
  // uids:    Iterable<uniqueId>
  applyForUids(adapter, uids) {
    if (!adapter || !uids) return;

    // Group changes by mesh so we set needsUpdate once per mesh, not
    // once per uid. Also handles the case where a uid has multiple
    // slots in the same mesh (rare but possible if InstancedMesh
    // ever fans out a uid).
    //
    // We remember WHICH slots were touched, not just that the mesh was:
    // `needsUpdate` re-uploads the whole instance buffer, so highlighting
    // a few elements in a pooled mesh of 512 pays for all 512. See
    // instanceUploadRanges — a spatially coherent selection then uploads
    // bytes, and a selection spread through the tile falls back to the
    // whole buffer, which is what it costs today anyway.
    const dirtyMeshes = new Map();   // mesh -> touched slot indices

    for (const uid of uids) {
      // The active record is what's currently in viewer.meshesObject.
      // Hidden duplicates in other tiles also need updating — when
      // the user hides a uid, ALL of that uid's slots across all
      // tiles need to go zero-scale (otherwise tile boundary cleanup
      // un-hides them via promotion).
      this._applyForUidAcrossTiles(adapter, uid, dirtyMeshes);
      // A NESTED FAMILY IS DRAWN AS ITS PARTS (./nestedParts). A hanger,
      // a trapeze, a beam clamp has no slot under its own uid - every
      // piece of it is a row under the piece's uid - so a change to the
      // ELEMENT walked zero slots and painted nothing. The store already
      // answers a part's colour from its element on the read
      // (getHighlight), which is what makes a tile streaming in later
      // arrive painted; this is the other half, the repaint NOW of the
      // parts already on screen. Owner via Tom, 2026-10-15: "can't select
      // hangers, trapeze and beam clamps". One Map lookup per uid; null
      // for every ordinary element.
      const parts = partsOf(uid);
      if (parts) for (const part of parts) this._applyForUidAcrossTiles(adapter, part, dirtyMeshes);
    }

    for (const [mesh, touched] of dirtyMeshes) {
      const capacity = mesh.isBatchedMesh || mesh.isSlotMesh
        ? (mesh.batchItemCount || mesh.instanceMatrix.count || 0)
        : (mesh.instanceMatrix.count || mesh.count || 0);
      applyRanges(mesh.instanceMatrix, touched, capacity, 16);
      if (mesh.instanceColor) applyRanges(mesh.instanceColor, touched, capacity, 3);
      markSceneChanged('applyForUids');
    }
  }

  // Walk only the slots known to host `uid`, looked up via the adapter's
  // reverse index (`getActiveSlotsForUid`). This replaces the previous
  // O(uids x tiles^2) double-scan (iterate every tile, then reverse-
  // lookup the tile key per slot) with O(slotsForUid) — typically 1-6
  // per uid given the single-active-instance invariant.
  _applyForUidAcrossTiles(adapter, uid, dirtyMeshes) {
    if (!adapter.activeByTile) return;

    const entries = adapter.getActiveSlotsForUid
      ? adapter.getActiveSlotsForUid(uid)
      : null;
    if (!entries || entries.length === 0) return;

    const owner = adapter.activeUidOwner.get(uid);
    const activeTileKey = owner ? owner.tileKey : null;

    for (const { tileKey, slotIndex } of entries) {
      const tileEntry = adapter.activeByTile.get(tileKey);
      if (!tileEntry) continue;
      const slots = tileEntry.slotsByUid.get(uid);
      if (!slots) continue;
      const slot = slots[slotIndex];
      if (!slot) continue;

      const mesh = slot.mesh;
      const userData = mesh.userData;
      const snapshot = userData?.snapshotMatrices;
      if (!snapshot) continue;

      const isActiveSlot = activeTileKey === tileKey;
      if (isActiveSlot) {
        this._applyOne(mesh, slot.index, snapshot, userData);
      } else {
        // Inactive duplicate — must stay zero-scale (single-active-
        // instance invariant). We explicitly zero the matrix here in
        // case a prior applyAll (without adapter) left a non-zero
        // matrix on this slot — and update color so promotion later
        // picks up the right highlight without an extra apply pass.
        this._tmpMatrix.copy(ZERO_MATRIX);
        mesh.setMatrixAt(slot.index, this._tmpMatrix);
        this._applyColorOnly(mesh, slot.index, userData);
      }
      let touched = dirtyMeshes.get(mesh);
      if (!touched) { touched = []; dirtyMeshes.set(mesh, touched); }
      touched.push(slot.index);
    }
  }

  // ============================================================
  // PER-INSTANCE WRITE — composed effective state for one slot
  // ============================================================

  _applyOne(mesh, index, snapshot, userData, adapter = null) {
    const entry = userData[index];
    if (!entry) return;

    const uid = entry.uniqueId;
    const disabledByExporter = !!entry.disabled;

    // Single source of truth: composeSlotState enforces precedence
    //   hidden > isolated-out > exporter-disabled > ghosted > highlight
    // and decides matrix + color in one place.
    const composed = this.composeSlotState(uid, disabledByExporter, userData.hash);

    // === SINGLE-ACTIVE-INSTANCE INVARIANT ===
    //
    // The octree exporter routes an instance into every tile its AABB
    // straddles, so the pool can hold N slots for the same uid. Only
    // ONE of them should render — the slot owned by the adapter's
    // `activeUidOwner`. Other slots are "duplicate" and must stay
    // matrix-zeroed regardless of store visibility, else the same
    // geometry renders multiple times (Z-fighting + "hide doesn't
    // hide" bug: hide writes zero to the active slot only, leaving
    // duplicates visible from a previous applyAll sweep).
    //
    // Without an adapter we can't enforce this here, so duplicates
    // can leak through a full sweep. Callers that have an adapter
    // (always true on the streaming path) should pass it.
    let isActiveSlot = true;
    if (adapter && adapter.activeUidOwner) {
      const owner = adapter.activeUidOwner.get(uid);
      if (owner) {
        isActiveSlot = (owner.tileKey === entry.tileKey);
      } else {
        // No owner means this uid isn't currently rendered. Treat
        // every slot as non-active (matrix-zero). Color still writes
        // its base so promotion later starts from a clean state.
        isActiveSlot = false;
      }
    }

    // ---- matrix ----
    const matrixVisible = composed.visible && isActiveSlot;
    if (matrixVisible) {
      this._tmpMatrix.fromArray(snapshot, index * 16);
    } else {
      this._tmpMatrix.copy(ZERO_MATRIX);
    }
    mesh.setMatrixAt(index, this._tmpMatrix);

    // ---- color ----
    this._writeColor(mesh, index, userData, composed);
  }

  // Color-only path used for non-active duplicate slots — they stay
  // matrix-zeroed by the adapter (single-active-instance invariant),
  // but their color buffer needs the right entry so promotion later
  // doesn't surface a stale color.
  _applyColorOnly(mesh, index, userData) {
    if (!mesh.setColorAt) return;
    const entry = userData[index];
    if (!entry) return;

    const uid = entry.uniqueId;
    const disabledByExporter = !!entry.disabled;
    const composed = this.composeSlotState(uid, disabledByExporter, userData.hash);
    this._writeColor(mesh, index, userData, composed);
  }

  // Internal: write the per-instance color based on composed state.
  // - invisible        → no color write needed (geometry is matrix-
  //                      zeroed anyway). We still write base to keep
  //                      the buffer consistent across promotions.
  // - ghosted + hl     → blended (handled inside composeSlotState).
  // - ghosted          → gray.
  // - highlighted      → highlight color.
  // - else             → baseColors[i].
  _writeColor(mesh, index, userData, composed) {
    if (!mesh.setColorAt) return;
    const baseColors = userData.baseColors;

    if (composed.highlightColor) {
      // Both plain highlight and ghosted-highlight come through
      // composeSlotState already correctly blended.
      //
      // A state colour WINS rather than tinting: every path multiplies the
      // element's own COLOR_0 vertex colour, so a black element tinted
      // green stays black and a dark one goes muddy. Marking it final is
      // what makes green look green on a black pipe and on a white wall.
      // The GHOSTED colour below is deliberately left multiplicative - it
      // is meant to read as inert, not as a state, and the screen-door
      // halftone carries that meaning without needing the colour.
      markFinal(this._tmpColor.copy(composed.highlightColor));
    } else if (composed.ghosted) {
      this._tmpColor.copy(GHOSTED_COLOR);
    } else if (baseColors) {
      this._tmpColor.fromArray(baseColors, index * 3);
    } else {
      this._tmpColor.copy(DEFAULT_COLOR);
    }

    mesh.setColorAt(index, this._tmpColor);
    // The tint alone never read as "inert" on a dark material; the
    // shader's screen-door ghost does (ghostMaterial.js).
    writeGhostAt(mesh, index, !!composed.ghosted);
  }

  // ============================================================
  // SUBSCRIBE TO STORE — wire applicator into store events
  // ============================================================

  // Returns an unsubscribe function. Caller (StreamingLoader) calls
  // this once after constructing both store and adapter.
  bindToStore(adapter) {
    return this.store.subscribe(({ type, uids }) => {
      if (uids === null) {
        // Full-sweep marker — re-apply across every active mesh.
        this._fullSweep(adapter);
      } else {
        this.applyForUids(adapter, uids);
      }
    });
  }

  _fullSweep(adapter) {
    if (!adapter.activeByTile) return;
    const seen = new Set();
    for (const [, tileEntry] of adapter.activeByTile) {
      for (const mesh of tileEntry.fragments) {
        if (seen.has(mesh)) continue;
        seen.add(mesh);
        // Pass adapter so applyAll enforces the single-active-
        // instance invariant. Without this, full-sweep events
        // (isolate toggle, materialize batch end) un-hide
        // duplicate slots and the previously-hidden geometry
        // re-appears in cross-tile positions — root cause of
        // the "hide doesn't fully hide" + "isolate shows wrong
        // meshes" symptoms.
        this.applyAll(mesh, adapter);
      }
    }
  }
}